import {debounce} from 'lodash';

import {safeSessionStorage} from '../../util/localStorage';

const PERSIST_DEBOUNCE_MS = 400;
const PERSIST_MAX_WAIT_MS = 1000;

/**
 * createDebouncedDraftStorage builds a zustand persist storage over
 * sessionStorage. Writes debounce so a keystroke burst writes once, maxWait
 * caps the gap during continuous typing, and a pagehide flush saves a draft
 * when the tab reloads inside the debounce window.
 */
export function createDebouncedDraftStorage() {
  // Pending writes are keyed so a burst coalesces to one flush per key and a
  // write to one key never drops another key's pending write.
  const pending = new Map<string, string>();
  const flush = () => {
    for (const [name, value] of pending) {
      safeSessionStorage.setItem(name, value);
    }
    pending.clear();
  };

  const scheduleFlush = debounce(flush, PERSIST_DEBOUNCE_MS, {
    maxWait: PERSIST_MAX_WAIT_MS,
    trailing: true,
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flush);
  }

  return {
    getItem: (name: string) => safeSessionStorage.getItem(name),
    setItem: (name: string, value: string) => {
      pending.set(name, value);
      scheduleFlush();
    },
    removeItem: (name: string) => {
      // Drop any pending write to this key so a debounced flush can't land
      // after the delete and resurrect it.
      pending.delete(name);
      safeSessionStorage.removeItem(name);
    },
  };
}
